import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import React from "react";
import { Delete, Download, CloudUpload, Description } from "@mui/icons-material";
import {
  Box,
  Typography,
  Tooltip,
  Button,
  List,
  ListItem,
  Snackbar,
  Alert,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import theme from "../../../style/theme";
import { FormProvider, useForm } from "react-hook-form";
import { FormTextField } from "../../../components/FormComponent/FormComponent";
import { ApplicationData, ResponseActivity } from "../ApplicationData";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../context/UserContext";

export type ReportAndUploadProps = {
  responseActivity: ResponseActivity;
  handleSaveReportData: (report: string, files: File[], responseActivity: ResponseActivity) => void;
};


type ReportAndUploadFormType = {
  report: string;
};

/**
 * レポート入力・ファイルアップロードコンポーネント
 * - 応答アクティビティに対するレポートテキストの入力とファイルのドラッグ&ドロップ/選択アップロードを提供する
 * - props: responseActivity (対応する応答アクティビティ),
 *          handleSaveReportData (レポートデータ保存コールバック)
 */
export const ReportAndUpload = (props: ReportAndUploadProps) => {
  const { responseActivity } = props;
  const handleAction = async (
    actionName: string, // 操作名を画面遷移際のactionとして伝達
    operation: () => Promise<boolean> // 特定処理するこための操作メソッド
  ) => {
    try {
      const result = await operation(); // 操作メソッドを実行
      if (result) {
        // 操作成功の場合は次の画面に遷移
        navigate("/OperationCompleted", { state: { action: actionName } });
      } else {
        console.log(`${actionName} 操作に失敗しました。`);
      }
    } catch (error) {
      console.error(`${actionName} 操作異常：`, error);
    }
  };
  const form = useForm<ReportAndUploadFormType>({
    defaultValues: { report: "" },
  });
  const navigate = useNavigate();
  const userData = useUser();

  const [reportHistory, setReportHistory] = useState<Array<{ text: string }>>(
    []
  );
  const [showSnackbar, setShowSnackbar] = useState(false); // スナックバーの表示状態を管理
  const [isSubmitted, setIsSubmitted] = useState(false); // レポートが送信済みかを管理
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]); // 状態管理：アップロードファイルリスト
  const [hoveredFileIndex, setHoveredFileIndex] = useState<number | null>(null); // ホバー中のファイルインデックスを保持
  // const [viewFileContent, setViewFileContent] = useState<string | null>(null); // 閲覧するファイルの内容を保持



  const handleCloseSnackbar = () => {
    setShowSnackbar(false); // スナックバーを閉じる
  };

  // ファイルアップロード処理
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setUploadedFiles([...uploadedFiles, ...Array.from(event.target.files)]);
    }
  };

  // ファイルを開く（直接ファイルを開く）
  const handleOpenFile = (file: File) => {
    const url = URL.createObjectURL(file); // ファイルの一時URLを生成
    // window.open(url); // ブラウザでファイルを開く（ローカルでサポートされているファイル形式の場合）

    // ファイルを直接開き、ブラウザで開くことを試みる
    const link = document.createElement("a");
    link.href = url;
    //  link.target = '_blank';  // ブラウザで新しいタブを開く
    link.download = file.name; // ファイルの元の名前でダウンロード
    link.click();
  };

  // ファイル削除処理
  const handleDeleteFile = (index: number) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  }; // ファイルダウンロード処理（模擬）

  const handleDownloadFile = (file: File) => {
    // ダウンロードの模擬操作（実際のダウンロードリンクに置き換えること）
    const url = URL.createObjectURL(file); // ファイルの一時URLを生成
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name; // ダウンロードファイル名を設定
    link.click();
    URL.revokeObjectURL(url); // 一時URLを解放
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const fakeEvent = {
        target: {
          files: createFileList(acceptedFiles),
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileUpload(fakeEvent);
    },
    [handleFileUpload]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: true,
  });

  // File[] → FileList を生成（React は FileList を要求）
  const createFileList = (files: File[]): FileList => {
    const dataTransfer = new DataTransfer();
    files.forEach((file) => dataTransfer.items.add(file));
    return dataTransfer.files;
  };

  const isMobile = useMediaQuery("(max-width:600px)"); // 600px以下の場合はスマートフォンデバイス

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit((data) => props.handleSaveReportData(data.report, uploadedFiles, responseActivity))}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            mx: isMobile ? 1 : 4,
            my: 2,
            p: "10px 40px",
            position: "center",
            border: "1px solid #ccc",
            borderRadius: "2px",
            backgroundColor: theme.palette.secondary.main,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              mt: 2,
            }}
          >
            {/* 作業報告表 */}
            <Typography variant="h3" sx={{ mb: 1 }}>
              {responseActivity?.appActivityName}
            </Typography>

            {/* 作業報告入力欄*/}
            {!isSubmitted ? (
              <>
                <Box sx={{ display: "flex", width: "100%", gap: 2 }}>
                  <FormTextField
                    name="report"
                    label="内容を入力"
                    variant="outlined"
                    multiline
                    rows={4}
                    sx={{
                      mb: 1,
                      backgroundColor: "#FFFFFF",
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "gray",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary",
                      },
                    }}
                    rules={{ require: "内容を入力してください" }}
                  />
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  width: "100%",
                  border: "1px solid gray",
                  padding: "10px",
                  pl: 0,
                  marginTop: 1,
                  whiteSpace: "pre-wrap",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  【内容】:
                </Typography>

                <Typography variant="body2">{reportHistory[0].text}</Typography>
              </Box>
            )}

            <Snackbar
              open={showSnackbar}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity="success"
                sx={{
                  position: "relative",
                  bottom: 20,
                  textAlign: "center",
                }}
              >
                完了しました。
              </Alert>
            </Snackbar>
          </Box>

          <Box sx={{ width: "100%", textAlign: "left" }}>
            <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
              アップロード
            </Typography>
            <Box
              {...getRootProps()}
              sx={{
                p: 2,
                border: "2px dashed #3f51b5",
                borderRadius: "8px",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: "#fff",
                "&:hover": {
                  backgroundColor: "#f0f4ff",
                },
              }}
            >
              {/* hidden input 用（Dropzone が hook 経由でバインド） */}
              <input {...getInputProps()} />
              <Button
                variant="contained"
                color="primary"
                component="label"
                startIcon={<CloudUpload />}
                sx={{ width: isMobile ? "100%" : undefined }}
              >
                ファイルを選択
                <input
                  type="file"
                  name="filePath"
                  multiple
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                />
              </Button>
              <Typography variant="h6" sx={{ mt: 2 }}>
                クリックしてファイルを選択
              </Typography>
            </Box>
            {uploadedFiles.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6">アップロードされたファイル:</Typography>
                <List>
                  {uploadedFiles.map((file, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        mt: 1,
                        "&:hover": {
                          cursor: "pointer",
                          textDecoration: "underline",
                        },
                      }}
                      onMouseEnter={() => setHoveredFileIndex(index)}
                      onMouseLeave={() => setHoveredFileIndex(null)}
                    >
                      {/* アイコン + ファイル名 + 削除ボタンを横並びに表示 */}
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {/* ファイルアイコン */}
                        <Description sx={{ mr: 1 }} />

                        {/* ファイル名 */}
                        <Typography
                          onClick={() => handleOpenFile(file)}
                          sx={{
                            mr: 2,
                            "&:hover": {
                              cursor: "pointer",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {file.name}
                        </Typography>

                        {/* 削除ボタン */}
                        <Tooltip title="削除">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteFile(index)}
                            size="small"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 1,
            }}
          >
            <Button
              variant="contained"
              type="submit"
              color="primary"
              sx={{ width: isMobile ? "100%" : undefined }}
            >
              完了
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
